import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { LOGO_ICON_SRC } from "@/lib/brand";

/**
 * Watermark visibility tiers. The watermark is sized as a centered
 * emblem at `min(1152px, 86.4vw)` and anchored at the visual center
 * of the workspace area via `top: calc(50% + 3.90625rem)` — that
 * offset is exactly half the chrome height (TopBar + Work Pulse =
 * 7.8125rem total), so the img midpoint lands on the midpoint of
 * the visible workspace below the chrome rather than on the raw
 * viewport center.
 *
 * Opacity is now 1.0 across all tiers so the logo reads at full
 * natural strength as a dominant brand stamp behind the workspace.
 * The img has a soft `drop-shadow(0 24px 48px rgba(0,0,0,0.55))`
 * filter so the silhouette lifts off the dark grey body with a
 * sense of depth, and the vignette overlay above has been lightened
 * to a barely-there fade (see the BlueprintBackdrop docblock).
 * Together this is the "max pop" configuration.
 *
 * If the watermark ends up overpowering UI text/content readability,
 * the dial-back order is:
 *   1. Drop these tiers (try 0.85 or 0.75 first).
 *   2. Re-enable the brightness filter (`"brightness(0.85)"`).
 *   3. Bring back a soft vignette.
 *
 * The density tiers remain in the API surface (and the
 * `useBlueprintDensity` hook still works as documented) so future
 * pages can opt into a louder/softer stamp without architectural
 * changes, but their values are uniform today.
 */
type Density = "default" | "open" | "dense";

const DENSITY_OPACITY: Record<Density, number> = {
    default: 1.0,
    open: 1.0,
    dense: 1.0,
};

interface DensityCtx {
    density: Density;
    setDensity: (d: Density) => void;
}

const Ctx = createContext<DensityCtx | null>(null);

/**
 * Provider mounted at the office app shell root. The single piece of
 * state is the active density; pages mutate it via `useBlueprintDensity`
 * and the backdrop reads it. Outside this provider (portal-shell,
 * login, public routes) the backdrop is never rendered, so customer-
 * facing screens stay branding-clean.
 */
export function BlueprintDensityProvider({ children }: { children: ReactNode }) {
    const [density, setDensity] = useState<Density>("default");
    return <Ctx.Provider value={{ density, setDensity }}>{children}</Ctx.Provider>;
}

/**
 * Page opt-in. Call once at the top of a page component:
 *
 *     export function DispatchShell() {
 *         useBlueprintDensity("open");
 *         ...
 *     }
 *
 * On mount, sets the active density. On unmount, restores "default".
 * No-op when rendered outside `BlueprintDensityProvider`.
 */
export function useBlueprintDensity(density: Density) {
    const ctx = useContext(Ctx);
    useEffect(() => {
        if (!ctx) return;
        ctx.setDensity(density);
        return () => ctx.setDensity("default");
    }, [ctx, density]);
}

/**
 * Pure white at 3% alpha — a clean, professional grid line tone.
 * Renders as the lightest possible architectural tracery on the
 * dark grey body. Behind cards the grid is hidden anyway since
 * cards sit on top, so the tracery only registers in workspace
 * negative space between cards.
 *
 * Tuning (tested-good range is 0.02–0.04):
 *   - Quieter: drop alpha to 0.02 — barely-there scaffold.
 *   - Louder:  bump to 0.04 — visible without competing.
 *   - Warmer:  swap to `rgba(240, 238, 235, 0.04)` (header cream).
 *   - Theme-token form: `color-mix(in srgb, var(--color-secondary) 6%, transparent)`.
 */
/**
 * LUMINANCE CEILING for the watermark (#1236, Corbin 2026-08-25).
 *
 * THE PROBLEM. Every contrast number in Cycle 1 — the PASS figures in #1220,
 * #1234 and `plan.md` — was computed against `--color-shell` as the flat colour
 * `#44403c`, relative luminance **0.0522**. The shell is not a flat colour. It
 * is a photographic watermark with light tan regions, so text rendered straight
 * onto it has a ratio that depends on where it lands, and moves as content
 * reflows.
 *
 * MEASURED, not reasoned about. Sampling the composited pixels of the running
 * app across /tasks, /analytics and /jobs at 1560/1280/660/390, the lightest
 * pixel under the content column was **rgb(239, 198, 150), luminance 0.6094**.
 * Against it:
 *
 *     white shell text          claimed 10.27:1   actually  1.59:1
 *     shell-foreground/80        claimed 6.40:1   actually  1.01:1
 *     #1234's lane-footer green  claimed 7.32:1   actually  1.13:1
 *
 * 1.01:1 is not "weaker in places". It is invisible.
 *
 * THE CEILING. `brightness()` multiplies the image's channels, so the ceiling
 * this enforces is `0.6094 × f(BRIGHTNESS)` — and it is set so the worst pixel
 * lands **at or below the 0.0522 the committed numbers already assumed**. That
 * is the whole point of the decision: it makes those numbers true rather than
 * approximately true. The verified result is in
 * `threads/cycle-1-believable/proof/c1-1d-watermark/`; re-run
 * `measure.mjs` there if this value is ever changed.
 *
 * WHY THE IMAGE AND NOT A SCRIM. The Issue proposed scoping a dark scrim to the
 * content column so the shell kept its identity at the page edges. Measured, the
 * column is `min(1600px, 100vw)` and the watermark is `min(1152px, 86.4vw)` — so
 * **the column completely covers the watermark at every viewport from 390 to
 * 2200px**. There are no exposed edges; a scoped scrim and a global cap render
 * identical pixels. Filtering the image is the smaller change: `--color-shell`,
 * the blueprint grid and the watermark asset are all untouched, which is exactly
 * what D1 ruled out of scope.
 */
const WATERMARK_BRIGHTNESS = 0.3;

const GRID_INK = "rgba(255, 255, 255, 0.03)";

/**
 * Grid square edge in pixels. 40px reads as a tight architectural
 * tracery without becoming busy. Drop to 32px for denser feel,
 * raise to 56px for a more spacious workspace.
 */
const GRID_SIZE = "40px";

/**
 * Workspace chrome height — used to anchor the watermark below the
 * sticky header + Work Pulse strip so the icon's center aligns with
 * the visible workspace center, not the raw viewport center.
 *
 *   - Office TopBar:  h-[4.6875rem] = 75px (50% reduction; the cropped
 *                                          rectangular wordmark sits
 *                                          comfortably here)
 *   - Work Pulse:     ~50px (py-2.5 wrapper + chip content)
 *   - Total chrome:   ~125px = 7.8125rem
 *
 * Hardcoded for the office shell (the primary surface). For tech-only
 * users the FieldTopBar is shorter and there's no Work Pulse, so the
 * watermark sits a touch lower than ideal — acceptable because tech
 * pages are dense and rarely benefit from a centered stamp anyway.
 */
// Kept for reference — measured chrome offset for centered watermark.
// Currently unused; pages override stamp position individually.
// const CHROME_HEIGHT = "7.8125rem";

/**
 * Fixed background layer rendered behind the office app shell.
 * Three sibling layers stacked inside a single fixed wrapper:
 *
 *   1. Grid — two `linear-gradient` background-image layers drawn
 *      with `backgroundSize: 40px 40px`. Pure white at 3% alpha
 *      (`GRID_INK`). Renders perfectly uniform 40×40 squares across
 *      the entire viewport. Visible only in negative space between
 *      cards.
 *   2. Logo watermark — centered emblem sized `min(1152px, 86.4vw)`,
 *      anchored at the visual center of the workspace via
 *      `top: calc(50% + 3.90625rem)` (half-chrome offset). Rendered
 *      at full strength: `opacity: 1.0` plus a soft
 *      `drop-shadow(0 24px 48px rgba(0,0,0,0.55))` so the silhouette
 *      lifts off the dark grey body. The drop-shadow filter follows
 *      the PNG alpha channel, so the shadow traces the hex/G shape
 *      rather than a rectangular box.
 *   3. Vignette — `linear-gradient(to bottom, rgba(0,0,0,0.05),
 *      rgba(0,0,0,0.2))`. A barely-there bottom-edge fade so the
 *      grid and watermark don't bleed off the lower edge harshly.
 *      Lowered from the previous (0.15 → 0.45) range so it no longer
 *      competes with the now-full-strength watermark.
 *
 * Layering:
 *   - The wrapper is `pointer-events-none fixed inset-0 -z-10`, so
 *     every layer below stays behind every positioned UI element.
 *   - Inside the wrapper:
 *       grid div         — `zIndex: 0`
 *       watermark img    — `zIndex: 1`
 *       vignette overlay — `zIndex: 2`
 *     The vignette therefore paints on top of grid and watermark
 *     but the entire stack still sits at -10 vs the app shell.
 *
 * The shell's outer scroll container is `<main>`, so content scrolls
 * while this layer stays still.
 */
/**
 * Anchor the watermark either at the visual center of the workspace
 * (default — accounts for the office app's TopBar + Work Pulse chrome
 * via a half-chrome top offset) or at the raw viewport center
 * (`"viewport"` — used by the login page and any other chromeless
 * full-screen surface where there is no top chrome to compensate for).
 */
type VerticalAnchor = "workspace" | "viewport";

export function BlueprintBackdrop({
    verticalAnchor = "workspace",
}: {
    verticalAnchor?: VerticalAnchor;
} = {}) {
    const ctx = useContext(Ctx);
    const opacity = DENSITY_OPACITY[ctx?.density ?? "default"];
    const watermarkTop = verticalAnchor === "viewport" ? "50%" : "calc(50% + 3.90625rem)";

    return (
        <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10 select-none">
            <div
                className="absolute inset-0"
                style={{
                    zIndex: 0,
                    backgroundImage: `
                        linear-gradient(${GRID_INK} 1px, transparent 1px),
                        linear-gradient(90deg, ${GRID_INK} 1px, transparent 1px)
                    `,
                    backgroundSize: `${GRID_SIZE} ${GRID_SIZE}`,
                }}
            />
            <img
                src={LOGO_ICON_SRC}
                alt=""
                aria-hidden="true"
                decoding="async"
                loading="lazy"
                className="pointer-events-none select-none"
                style={{
                    position: "absolute",
                    top: watermarkTop,
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "min(1152px, 86.4vw)",
                    height: "auto",
                    objectFit: "contain",
                    opacity,
                    filter: `brightness(${WATERMARK_BRIGHTNESS}) drop-shadow(0 24px 48px rgba(0, 0, 0, 0.55))`,
                    transition: "opacity 300ms ease",
                    zIndex: 1,
                }}
            />
            <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                    zIndex: 2,
                    backgroundImage: "linear-gradient(to bottom, rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.2))",
                }}
            />
        </div>
    );
}
